import type { CbPaymentSource } from '@lmc/common/types'
import { Button, Text } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import React from 'react'

type PaymentDetailsProps = {
  paymentSource: CbPaymentSource
  openChargebee: () => void
}

const renderDirectDebit = ({
  paymentSource,
  openChargebee
}: PaymentDetailsProps) => {
  const isValid = paymentSource.status === 'valid'
  return (
    <div
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
    >
      <Button variant="default" onClick={openChargebee}>
        Direct Debit
      </Button>
      <Text
        as="p2"
        style={styles.leftMargin}
        color={isValid ? 'successgreen' : 'yellow'}
      >
        {isValid ? 'Valid' : 'Not Validated'}
      </Text>
    </div>
  )
}

const renderCardDetails = ({
  paymentSource,
  openChargebee
}: PaymentDetailsProps) => {
  const isValid = paymentSource.status === 'valid'

  const expiry = `${paymentSource.card?.expiry_month}/${paymentSource.card?.expiry_year}`
  const isExpired = dayjs(expiry, 'MM/YYYY').isBefore(dayjs().startOf('month'))
  const isExpiring = dayjs(expiry, 'MM/YYYY').isBefore(dayjs().add(2, 'month'))

  let warning
  if (!isValid) warning = paymentSource.status
  if (isExpiring) warning = 'Expiring'
  if (isExpired) warning = 'Expired'
  return (
    <div
      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
    >
      <div>
        <Button variant="default" onClick={openChargebee}>
          {`${paymentSource.card?.brand?.toUpperCase()} ${
            paymentSource.card?.funding_type
          } ending in ${paymentSource.card?.last4}. Expires ${expiry}.`}
        </Button>
      </div>
      {warning ? (
        <Text
          as="p2"
          style={styles.leftMargin}
          color="yellow"
          id="warning-text"
        >
          {warning}
        </Text>
      ) : (
        <Text
          as="p2"
          style={{
            ...styles.leftMargin,
            wordBreak: 'initial'
          }}
          color="successgreen"
          id="valid-text"
        >
          Valid
        </Text>
      )}
    </div>
  )
}

/**
 * Display the current status of the Chargebee Payment Source.
 * It flags if there are issues like expired cards.
 * @param paymentSource: object directly from the Chargebee API
 */
export const PaymentDetails = ({
  paymentSource,
  openChargebee
}: PaymentDetailsProps) => {
  // The user hasn't entered any payment details
  // We want to encourage them to add them in the Chargebee modal
  if (!paymentSource)
    return (
      <Button
        test-id="add-payment-btn"
        variant="text"
        onClick={openChargebee}
        style={{ width: 190 }}
      >
        Add payment method
      </Button>
    )

  // Direct Debits often take a while (1 week) to get validated
  // It's therefore important to display the "valid" status
  if (paymentSource.type === 'direct_debit') {
    return renderDirectDebit({ paymentSource, openChargebee })
  }

  // Cards often exipre, which we need to flag
  // In the future, we might be able to flag reasons
  // for failed cardpayments here, but that data need to be
  // fetched from stripe
  if (paymentSource.type === 'card' && paymentSource.card) {
    return renderCardDetails({ paymentSource, openChargebee })
  }

  return null
}

const styles = {
  leftMargin: { marginLeft: 6 }
}

export default PaymentDetails
